import {Injectable} from '@angular/core';
import {IUser} from '../../models/auth';
import {StorageService} from '../storage/storage.service';
import {ClinicState, IClinic} from '../../models/clinic';
import {LocalStorageEnum} from '../../enums/utils';
import {firstValueFrom} from 'rxjs';
import {Store} from '@ngxs/store';
import {RolesEnum} from '../../enums/role';
import {ICredentials} from '../../interfaces/credentials';
import {timeZonesData} from '../../config/data/time-zones/time-zones.data';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(
    private storageService: StorageService,
    private store: Store,
  ) { }


  /**
   * Get Client TimeZone
   * @param user optional user
   */
  getUserTimeZone(user?: IUser): string {
    const usr = user ?? this.storageService.getLocalItem<ICredentials>(LocalStorageEnum.USER).user;
    if (!usr) return Intl.DateTimeFormat().resolvedOptions().timeZone;

    const userTimeZone: string = timeZonesData.getUTCByNameTimeZone(usr.timeZone);
    if (!userTimeZone) return Intl.DateTimeFormat().resolvedOptions().timeZone;
    return userTimeZone;
  }

  getRoleByUser(optionalUser?: IUser): RolesEnum | undefined {
    const user = optionalUser ?? this.storageService.getLocalItem<ICredentials>(LocalStorageEnum.USER)?.user;
    if (!user) return;

    const clinic = this.store.selectSnapshot(ClinicState.getClinicSelected);
    if (!clinic) return;

    if (clinic.ownerId === user._id) return RolesEnum.ADMIN;
    if (user.experts?.length > 0) return RolesEnum.EXPERT;
    if (user.patients?.length > 0) return RolesEnum.PATIENT;
  }

  /**
   * this function get clinic id from user with relations
   * @param user LS user
   */
  getClinicIdFromUser(user: IUser): string | null {
    const [patientOrExpert] = user?.patients ? user.patients : user?.experts ? user.experts : [];
    return patientOrExpert.clinicId ?? null;
  }

  /**
   * this function get expert id from user with relations
   * @param user LS user
   */
  getExpertId(user: IUser): string | null {
    const [expert] = user.experts ?? [];
    return expert ? expert._id : null;
  }

  /**
   * this function get the expert id of the authenticated user
   */
  getExpertIdOfAuthUser(): string | null {
    const authUser = this.storageService.getLocalItem<ICredentials>(LocalStorageEnum.USER);
    if (!authUser) return null;

    const [expert] = authUser.user?.experts ?? [];
    return expert ? expert._id : null;
  }

  /**
   *
   * @param optionalUser user LS user
   * @param optionalClinic clinic redux
   */
  async getExpertIsAdmin(optionalUser?: IUser, optionalClinic?: IClinic): Promise<boolean> {
    const user: IUser = optionalUser ?? this.storageService.getLocalItem<{user: IUser, token: string}>(LocalStorageEnum.USER)?.user;
    if (!user || !user.experts || user.experts.length === 0) return false;


    const clinic = optionalClinic || await firstValueFrom(this.store.selectOnce(ClinicState.getClinicSelected));
    if (!clinic) return false;

    return clinic.ownerId === user._id;
  }

  /**
   *
   * @param optionalUser user LS user
   * @param optionalClinic clinic redux
   */
  async getIsOnlyExpert(optionalUser?: IUser, optionalClinic?: IClinic): Promise<boolean> {
    const user = optionalUser ?? this.storageService.getLocalItem<{user: IUser, token: string}>(LocalStorageEnum.USER)?.user;
    if (!user || !user.experts || user.experts.length === 0) return false;


    const clinic = optionalClinic || await firstValueFrom(this.store.selectOnce(ClinicState.getClinicSelected));
    if (!clinic) return false;

    return clinic.ownerId !== user._id;
  }

  async isBetaUser(): Promise<void> {

  }
}
